import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/soul";
import { useAuth } from "@/context/AuthContext";
import { SpeakButton } from "@/components/SpeakButton";
import { ShareButton } from "@/components/ShareButton";
import { toast } from "sonner";
import { Search, Globe, Loader2, Sparkles, BookOpen, BookmarkPlus } from "lucide-react";

export default function Words() {
  const { user } = useAuth();
  const isPaid = user && (user.tier === "seeker" || user.tier === "contributor");
  const [q, setQ] = useState("");
  const [words, setWords] = useState([]);
  const [active, setActive] = useState(null);

  const [term, setTerm] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    api.get("/words").then((r) => {
      setWords(r.data.words);
      setActive(r.data.words[0]);
    });
  }, []);

  const search = async (value) => {
    setQ(value);
    const r = await api.get(`/words/search?q=${encodeURIComponent(value)}`);
    setWords(r.data.results);
    if (r.data.results.length) setActive(r.data.results[0]);
    else setActive(null);
  };

  const lookup = async (e, override) => {
    if (e) e.preventDefault();
    const w = (override ?? term).trim();
    if (!w) return;
    setLoading(true);
    setError("");
    setLimitReached(false);
    setResult(null);
    try {
      const r = await api.post("/words/lookup", { word: w });
      setResult(r.data.result);
    } catch (err) {
      if (err.response?.status === 402) setLimitReached(true);
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not look up that word.");
    } finally {
      setLoading(false);
    }
  };

  const quickLook = (w) => {
    setTerm(w);
    lookup(null, w);
  };

  const saveWord = async () => {
    if (!result) return;
    try {
      await api.post("/lexicon", { word: result.word, result });
      toast.success(`"${result.word}" saved to your lexicon.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    }
  };

  return (
    <main data-testid="words-page" className="max-w-7xl mx-auto px-6 py-16">
      <div className="max-w-3xl mb-12 fade-up">
        <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4">Word Power Explorer</p>
        <h1 className="serif text-4xl sm:text-6xl font-light text-[#F5F1E7]">
          In the beginning <span className="italic-serif text-[#D4AF37]">was the Word.</span>
        </h1>
        <p className="mt-5 text-[#A8A29E] leading-relaxed text-lg">
          The words we speak gently shape the world we live in. Trace them back to their
          roots, and softly reclaim the power they hold over your life.
        </p>
        <Link
          to="/power-of-words"
          data-testid="words-to-pow"
          className="mt-4 inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:underline"
        >
          Why words are powerful — and how their study left our schools →
        </Link>
        <div>
          <Link
            to="/word-archive"
            data-testid="words-to-archive"
            className="mt-2 inline-flex items-center gap-2 text-sm text-[#A8A29E] hover:text-[#D4AF37] transition-colors duration-300"
          >
            Browse the Word of the Day archive →
          </Link>
        </div>
      </div>

      {/* AI word lookup */}
      <form onSubmit={lookup} className="max-w-2xl">
        <div className="glass rounded-full flex items-center gap-3 px-6 py-4 gold-glow">
          <Globe size={20} className="text-[#D4AF37]" />
          <input
            data-testid="word-lookup-input"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Type any word in any language — origins & meanings across the world..."
            className="bg-transparent outline-none w-full text-[#F5F1E7] placeholder:text-[#A8A29E]/60 text-lg"
          />
          <button
            data-testid="word-lookup-btn"
            type="submit"
            disabled={loading}
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#110D14] px-5 py-2 font-medium hover:bg-[#e6c860] transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {loading ? "Seeking" : "Reveal"}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <span className="text-xs text-[#A8A29E]">Try:</span>
          {["logos", "shalom", "namaste", "agape", "ubuntu", "selah"].map((w) => (
            <button
              key={w}
              type="button"
              data-testid={`word-suggest-${w}`}
              onClick={() => quickLook(w)}
              className="text-xs px-3 py-1 rounded-full border border-white/10 text-[#A8A29E] hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition-colors duration-300"
            >
              {w}
            </button>
          ))}
        </div>
      </form>

      {error && <p data-testid="word-lookup-error" className="mt-6 text-[#C1272D]">{error}</p>}
      {limitReached && (
        <Link data-testid="word-upgrade-cta" to="/pricing"
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#17100C] px-6 py-2.5 text-sm font-medium hover:bg-[#e6c860] transition-colors duration-300">
          Upgrade to Seeker for unlimited lookups
        </Link>
      )}
      {loading && (
        <div className="mt-8 glass rounded-2xl p-10 flex items-center gap-3 text-[#A8A29E]">
          <Loader2 size={20} className="animate-spin text-[#D4AF37]" />
          Tracing the roots across languages...
        </div>
      )}
      {result && !loading && <WordResult r={result} canSave={!!isPaid} onSave={saveWord} />}

      {/* Featured curated words */}
      <div className="mt-20 mb-6 flex items-center gap-3">
        <BookOpen size={18} className="text-[#D4AF37]" />
        <h2 className="serif text-3xl text-[#F5F1E7]">Featured reflections</h2>
      </div>
      <div className="glass rounded-full flex items-center gap-3 px-6 py-3 max-w-xl">
        <Search size={18} className="text-[#A8A29E]" />
        <input
          data-testid="word-search-input"
          value={q}
          onChange={(e) => search(e.target.value)}
          placeholder="Filter featured words..."
          className="bg-transparent outline-none w-full text-[#F5F1E7] placeholder:text-[#A8A29E]/60"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-12">
        {/* Word list */}
        <div className="lg:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto pr-2" data-testid="word-list">
          {words.map((w) => (
            <button
              key={w.term}
              data-testid={`word-item-${w.term}`}
              onClick={() => setActive(w)}
              className={`w-full text-left px-5 py-4 rounded-xl border transition-colors duration-300 ${
                active?.term === w.term
                  ? "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F5F1E7]"
                  : "border-white/5 text-[#A8A29E] hover:border-[#D4AF37]/30 hover:text-[#F5F1E7]"
              }`}
            >
              <span className="serif text-2xl">{w.term}</span>
            </button>
          ))}
          {words.length === 0 && (
            <p className="text-[#A8A29E] italic-serif text-lg px-2">No word found. Try another.</p>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {active ? (
            <div data-testid="word-detail" className="glass rounded-2xl p-8 sm:p-12">
              <h2 className="serif text-5xl sm:text-6xl text-[#F5F1E7]">{active.term}</h2>

              <div className="mt-8 space-y-7">
                <Field label="Origin" value={active.origin} />
                <Field label="Roots" value={active.roots} />
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Inner Meaning</p>
                  <p className="text-[#F5F1E7] text-xl italic-serif leading-relaxed">{active.meaning}</p>
                </div>
                <div className="border-t border-white/5 pt-6">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#A8A29E] mb-2">A Practice</p>
                  <p className="text-[#A8A29E] leading-relaxed">{active.practice}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="glass rounded-2xl p-12 text-[#A8A29E] italic-serif text-xl">
              Choose a word to explore its origin.
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.25em] text-[#A8A29E] mb-2">{label}</p>
      <p className="text-[#F5F1E7] leading-relaxed">{value}</p>
    </div>
  );
}

function WordResult({ r, canSave, onSave }) {
  const shareText = [
    `${r.word}${r.language_detected ? ` (${r.language_detected})` : ""}`,
    r.etymology ? `Origin: ${r.etymology}` : "",
    r.inner_meaning ? `Inner meaning: ${r.inner_meaning}` : "",
    "— discovered on Mirrors of Your Soul",
  ].filter(Boolean).join("\n\n");

  return (
    <div data-testid="word-lookup-result" className="mt-8 glass rounded-2xl p-8 sm:p-12 gold-glow">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-baseline gap-4">
          <h2 className="serif text-5xl sm:text-6xl text-[#F5F1E7]">{r.word}</h2>
          {r.pronunciation && <span className="text-[#A8A29E] text-lg">/{r.pronunciation}/</span>}
          {r.language_detected && (
            <span className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-3 py-1">
              {r.language_detected}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ShareButton
            testId="share-word-btn"
            title={`The word "${r.word}"`}
            text={shareText}
            label="Share this word"
          />
          {canSave && (
            <button
              data-testid="save-word-btn"
              onClick={onSave}
              className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors duration-300"
            >
              <BookmarkPlus size={16} /> Save to my lexicon
            </button>
          )}
        </div>
      </div>

      <div className="mt-4">
        <SpeakButton text={`${r.word}. I speak ${r.word} over my life.`} word={r.word} record label="Speak this word over yourself" />
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-8">
        <div className="space-y-6">
          {r.etymology && <Field label="Origin & Etymology" value={r.etymology} />}
          {Array.isArray(r.roots) && r.roots.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#A8A29E] mb-3">Roots</p>
              <div className="space-y-2">
                {r.roots.map((rt, i) => (
                  <div key={i} className="text-[#F5F1E7]">
                    <span className="text-[#D4AF37] serif text-xl">{rt.root}</span>
                    <span className="text-[#A8A29E] text-sm"> · {rt.language}</span>
                    <span className="text-[#A8A29E]"> — {rt.meaning}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {Array.isArray(r.meanings) && r.meanings.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#A8A29E] mb-3">Meanings</p>
              <ul className="space-y-1.5">
                {r.meanings.map((m, i) => (
                  <li key={i} className="text-[#F5F1E7] leading-relaxed flex gap-2">
                    <span className="text-[#D4AF37]">·</span> {m}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Across Languages</p>
          <div className="space-y-3 max-h-[380px] overflow-y-auto pr-2">
            {(r.across_languages || []).map((l, i) => (
              <div key={i} className="border-b border-white/5 pb-3">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[#F5F1E7] serif text-xl">{l.word}</span>
                  <span className="text-xs text-[#A8A29E] uppercase tracking-wide">{l.language}</span>
                </div>
                <p className="text-[#A8A29E] text-sm mt-1">{l.meaning}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {r.inner_meaning && (
        <div className="mt-8 border-t border-white/5 pt-6">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Inner Meaning</p>
          <p className="text-[#F5F1E7] text-xl italic-serif leading-relaxed">{r.inner_meaning}</p>
        </div>
      )}
      {r.scripture && (
        <p className="mt-4 italic-serif text-[#C1272D]">— {r.scripture}</p>
      )}
    </div>
  );
}
